import type { PropSpec, PropValue } from './types';

const VALID_TYPES = new Set(['text', 'number', 'boolean', 'enum']);

function coerceDefault(type: string, d: unknown): PropValue {
  if (d === undefined || d === null) {
    return type === 'number' ? 0 : type === 'boolean' ? false : '';
  }
  if (type === 'number') return typeof d === 'number' ? d : Number(d) || 0;
  if (type === 'boolean') return Boolean(d);
  return String(d);
}

/** Validate a raw `export const props` manifest into PropSpec[]. */
export function normalizeProps(raw: unknown): PropSpec[] {
  if (!raw || typeof raw !== 'object') return [];
  const out: PropSpec[] = [];
  for (const [name, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') continue;
    const type = (v as { type?: unknown }).type;
    if (typeof type !== 'string' || !VALID_TYPES.has(type)) continue;
    const spec: PropSpec = {
      name,
      type: type as PropSpec['type'],
      default: coerceDefault(type, (v as { default?: unknown }).default),
    };
    if (type === 'enum') {
      const opts = (v as { options?: unknown }).options;
      spec.options = Array.isArray(opts) ? opts.map(String) : [];
    }
    out.push(spec);
  }
  return out;
}

/** Collapse a schema to { name: default }. */
export function defaultsFrom(schema: PropSpec[]): Record<string, PropValue> {
  const out: Record<string, PropValue> = {};
  for (const s of schema) out[s.name] = s.default;
  return out;
}

/**
 * Extract the `export const props = { ... }` manifest from TSX source. Does NOT
 * compile or run the component module (no JSX, no React, no Blob/URL): it only
 * isolates the `props` object literal and evaluates THAT literal in a bare
 * Function scope (guarded by try/catch — a malformed literal yields undefined).
 * Returns undefined if absent or unparseable; callers keep the prior schema.
 */
export function readPropsManifest(src: string): unknown {
  const m = src.match(/export\s+const\s+props\s*=\s*(\{[\s\S]*?\n\s*\})/);
  if (!m) {
    // fallback: single-line object
    const m2 = src.match(/export\s+const\s+props\s*=\s*(\{[^\n]*\})/);
    if (!m2) return undefined;
    return evalObjectLiteral(m2[1]);
  }
  return evalObjectLiteral(m[1]);
}

function evalObjectLiteral(literal: string): unknown {
  try {
    // eslint-disable-next-line no-new-func
    return Function(`"use strict";return (${literal});`)();
  } catch {
    return undefined;
  }
}
